import React, { useState } from "react";
import "./bill-filter.style.scss";
import "../form-inputs/form-inputs.style.scss";

// packages
import Swal from "sweetalert2";
import type { FormikProps } from "formik";

// icons
import { FaBook, FaCopy, FaEraser, FaFileExport, FaMagnifyingGlass } from "react-icons/fa6";

// components
import TextField from "../form-inputs/text-field.component";
import SelectBox from "../form-inputs/select-box.component";
import MultipleTagsInput from "../form-inputs/multiple-tags-input.component";
import BillDuplicate from "./bill-duplicate.component";
import SalesBookOptionsDialog from "../sales-book-options-dialog/sales-book-options-dialog.component";
import InputDatePicker from "../input-date-picker/input-date-picker.component";
import FormError from "../form-error/form-error.component";
import InlineNotice from "../inline-notice/inline-notice.component";

// helper utility functions
import { formatEnDate } from "../../global/date.util";
import { getNepaliFiscalYears } from "../../global/function";
import { statusFilterToParams } from "./bill-filter.types";

// query hooks
import { useExportFile } from "../../queries/export.queries";
import { useSession } from "../../queries/auth.queries";
import { useInfiniteBills, usePrintSalesBook } from "../../queries/bill.queries";

// types
import type { BillSearchFilters, SalesBookDuration } from "../../queries/bill.queries";
import type { BillFilterFormValues, BillStatusFilter } from "./bill-filter.types";

interface BillFilterProps {
	filterFormik: FormikProps<BillFilterFormValues>;
	// Last-submitted (Search button), transformed filters — NOT
	// filterFormik.values live, since Export/Duplicate/Sales Book all operate
	// on "the currently applied search", not whatever's mid-edit in the form.
	activeFilters: BillSearchFilters;
	// The header stat pill currently selected (bill-list.component.tsx) — Export
	// needs this merged in separately since status is no longer part of
	// filterFormik.values (see statusFilterToParams).
	statusFilter: BillStatusFilter;
}

const fiscalYearOptions = getNepaliFiscalYears();

function BillFilter({ filterFormik, activeFilters, statusFilter }: BillFilterProps) {
	const { data: session } = useSession();
	const [billSideModal, setBillSideModal] = useState<"duplicate" | null>(null);
	const [salesBookDialogOpen, setSalesBookDialogOpen] = useState(false);
	const [salesBookNotice, setSalesBookNotice] = useState<string | null>(null);

	const exportMutation = useExportFile();
	const printSalesBookMutation = usePrintSalesBook();

	// Same queryKey+params as bill-list.component.tsx's own useInfiniteBills
	// call — React Query dedupes these into a single shared request/cache
	// entry, so this costs nothing extra; it's only here to read
	// filteredTotal for the button-disabled checks below.
	const { data } = useInfiniteBills(activeFilters);
	const filteredTotal = data?.pages[0]?.filteredTotal ?? 0;
	const maxSales = data?.pages[0]?.maxSales || 10000;

	const handleBillsExport = async () => {
		Swal.fire({
			title: "Export Bills",
			text: "All your filtered bills will be exported to excel file. Are you sure?",
			icon: "info",
			showDenyButton: true,
			confirmButtonText: "Export",
			denyButtonText: "Cancel",
			customClass: {
				popup: "alert-modal",
				actions: "btn-container",
				confirmButton: "alert-btn btn-confirm",
				denyButton: "alert-btn btn-cancel",
			},
		}).then((result) => {
			if (result.isConfirmed && !exportMutation.isPending) {
				exportMutation.mutate({
					tableName: "bill",
					filterValues: {
						...filterFormik.values,
						...statusFilterToParams(statusFilter),
					} as unknown as Record<string, unknown>,
					companyId: session?.company_id,
				});
			}
		});
	};

	const handleMinValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(event.target.value);
		if (value < filterFormik.values.total_sales_max) {
			filterFormik.setFieldValue("total_sales_min", value);
		} else {
			filterFormik.setFieldValue("total_sales_min", filterFormik.values.total_sales_max);
		}
	};

	const handleMaxValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(event.target.value);
		if (value > filterFormik.values.total_sales_min) {
			filterFormik.setFieldValue("total_sales_max", value);
		} else {
			filterFormik.setFieldValue("total_sales_max", filterFormik.values.total_sales_min);
		}
	};

	const handleSalesBookConfirm = (duration: SalesBookDuration, useEnDate: boolean) => {
		printSalesBookMutation.mutate(
			{ duration, useEnDate },
			{
				onSuccess: () => {
					setSalesBookDialogOpen(false);
					setSalesBookNotice("PDF opened in a new tab.");
				},
			},
		);
	};

	function handleTagAdd(e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) {
		const target = e.target as HTMLInputElement;
		const value = target.value;
		if (!value.trim()) return;
		const newValue = value?.split("#").filter(Boolean);

		const set1 = new Set(filterFormik.values?.bill_for);
		const set2 = new Set(newValue);
		const mergedSet = new Set([...set1, ...set2]);
		const resultArray = Array.from(mergedSet);

		filterFormik.setFieldValue("bill_for", resultArray);
		target.value = "";
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key !== "Enter") return;
		handleTagAdd(e);
	}

	function handleTagRemove(index: number) {
		const tags = [...filterFormik.values?.bill_for];
		filterFormik.setFieldValue(
			"bill_for",
			tags.filter((_el, i) => i !== index),
		);
	}

	const isDateDisabled = filterFormik.values.fiscal_year !== "";

	return (
		<>
			<div className="filter-container">
				<div className="filter-container-inner">
					<div className="filter-items-container">
						<h2 className="filter-section-header">Filter Your Bills</h2>
						<p className="action-description">
							The export to excel and duplicate bills are based on filtered data.
						</p>
						<div className="action-row">
							<div
								className="action-btn"
								aria-disabled={exportMutation.isPending || filteredTotal < 1}
								onClick={handleBillsExport}
							>
								<FaFileExport />Export
							</div>
							<div
								className="action-btn"
								aria-disabled={filteredTotal < 1}
								onClick={() => setBillSideModal("duplicate")}
							>
								<FaCopy />Duplicate
							</div>
						</div>
						<FormError
							isError={exportMutation.isError}
							error={exportMutation.error}
							fallback="Failed to export file."
						/>
						<InlineNotice
							message={exportMutation.isSuccess ? "File downloaded" : null}
							type="success"
							autoHideMs={3000}
							onDismiss={() => exportMutation.reset()}
						/>
						<div className="action-row">
							<div className="action-btn" onClick={() => setSalesBookDialogOpen(true)}>
								<FaBook />Generate Sales Book
							</div>
						</div>
						<InlineNotice
							message={salesBookNotice}
							type="success"
							autoHideMs={3000}
							onDismiss={() => setSalesBookNotice(null)}
						/>
					</div>
					<div className="filter-items-container">
						<MultipleTagsInput
							label="Invoice Tags"
							name="bill_for"
							onKeyDown={handleKeyDown}
							onBlur={handleTagAdd}
							onTagRemove={handleTagRemove}
							tagsArray={filterFormik.values?.bill_for}
							autoComplete="off"
						/>
					</div>
					<div className="filter-items-container">
						<div className="filter-items-label">Invoice Details</div>
						<div className="input-row">
							<TextField
								name="invoice_number"
								label="Invoice Number"
								placeholder="e.g. A0001 or 0001"
								formik={filterFormik}
							/>
							<div className="input-item">
								<label className="input-label">Fiscal Year</label>
								<SelectBox
									name="fiscal_year"
									onChange={filterFormik.handleChange}
									onBlur={filterFormik.handleBlur}
									value={filterFormik.values?.fiscal_year || ""}
									options={fiscalYearOptions}
								/>
							</div>
						</div>
						<div className="input-item">
							<InputDatePicker
								label="Invoice Date Range"
								selectionMode="range"
								rangeValue={
									!isDateDisabled
										? {
												start: filterFormik.values.date_from
													? new Date(filterFormik.values.date_from)
													: null,
												end: filterFormik.values.date_to
													? new Date(filterFormik.values.date_to)
													: null,
											}
										: { start: null, end: null }
								}
								onRangeChange={(result) => {
									filterFormik.setFieldValue(
										"date_from",
										formatEnDate(result.start.en, "YYYY-MM-DD"),
									);
									filterFormik.setFieldValue(
										"date_to",
										result.end
											? formatEnDate(result.end.en, "YYYY-MM-DD")
											: "",
									);
								}}
								clearable
								onClear={() => {
									filterFormik.setFieldValue("date_from", "");
									filterFormik.setFieldValue("date_to", "");
								}}
								disabled={isDateDisabled}
								error={
									filterFormik.submitCount > 0
										? (filterFormik.errors.date_from ?? filterFormik.errors.date_to)
										: undefined
								}
							/>
						</div>
					</div>
					<div className="filter-items-container">
						<div className="filter-items-label">Customer Details</div>
						<div className="input-row">
							<TextField
								name="customer_id"
								label="Customer Id"
								placeholder="e.g. 7 or CUS_0007"
								formik={filterFormik}
							/>
							<TextField
								name="buyer_name"
								label="Customer Name"
								placeholder="Customer Name"
								formik={filterFormik}
							/>
						</div>
						<TextField
							name="buyer_pan"
							label="Customer PAN"
							placeholder="Customer PAN"
							formik={filterFormik}
						/>
					</div>
					<div className="filter-items-container">
						<div className="filter-items-label">Total Sales Range</div>
						<div style={{ padding: "20px 0", overflow: "hidden" }}>
							<div className="input-slider">
								<div
									className="input-progress"
									style={{
										left: (filterFormik.values?.total_sales_min / maxSales) * 100 + "%",
										right: 100 - (filterFormik.values?.total_sales_max / maxSales) * 100 + "%",
									}}
								></div>
							</div>
							<div className="range-input">
								<input
									name="total_sales_min"
									type="range"
									className="range-min"
									min={0}
									max={maxSales}
									onChange={handleMinValueChanged}
									value={filterFormik.values?.total_sales_min || 0}
								/>
								<input
									name="total_sales_max"
									type="range"
									className="range-max"
									min={0}
									max={maxSales}
									onChange={handleMaxValueChanged}
									value={filterFormik.values?.total_sales_max || 0}
								/>
							</div>
							{filterFormik.submitCount > 0 && (
								<div className="validation-error">
									{filterFormik?.errors?.total_sales_min || filterFormik?.errors?.total_sales_max}
								</div>
							)}
						</div>

						<div className="filter-row">
							<div
								className="input-item"
								style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}
							>
								Rs.
								<input
									name="total_sales_min"
									className="input-box-wrapper"
									type="number"
									onChange={filterFormik.handleChange}
									onBlur={filterFormik.handleBlur}
									value={filterFormik.values?.total_sales_min}
								/>
							</div>
							<div className="range-divider" />
							<div
								className="input-item"
								style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}
							>
								Rs.
								<input
									name="total_sales_max"
									className="input-box-wrapper"
									type="number"
									onChange={filterFormik.handleChange}
									onBlur={filterFormik.handleBlur}
									value={filterFormik.values?.total_sales_max}
								/>
							</div>
						</div>
					</div>
					<div className="filter-btn-container">
						<div className="filter-btn inverted" onClick={() => filterFormik.resetForm()}>
							<FaEraser />Reset all filters
						</div>
						<div className="filter-btn" onClick={() => filterFormik.submitForm()}>
							<FaMagnifyingGlass />
							Search
						</div>
					</div>
				</div>
			</div>
			<BillDuplicate
				isOpen={billSideModal === "duplicate"}
				onDuplicateModalClose={() => setBillSideModal(null)}
				filterValues={activeFilters}
			/>
			<SalesBookOptionsDialog
				open={salesBookDialogOpen}
				onOpenChange={setSalesBookDialogOpen}
				isPending={printSalesBookMutation.isPending}
				isError={printSalesBookMutation.isError}
				error={printSalesBookMutation.error}
				onConfirm={handleSalesBookConfirm}
			/>
		</>
	);
}

export default BillFilter;
